/* eslint-disable no-sparse-arrays */
import React from 'react';
import { Link } from 'react-router-dom';
import accounting from 'accounting';
import moment from 'moment';
import _ from 'underscore';
import { unescapeHTML, titleize } from 'underscore.string';
import geolib from 'geolib';
import { Translation } from 'react-i18next';
import {
  LoadDimensionFormatter,
  LoadUnitFormatter,
} from '../master/formatters';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';
import formatMoney from 'utils/formatters/Money';

const distanceFormatter = (cell) => (
  <span>
    {cell ? geolib.convertUnit(DEFAULT_DISTANCE_UNIT, cell) : '-'}{' '}
    {DEFAULT_DISTANCE_UNIT}
  </span>
);

const addressFormatter = (cell) => {
  const label = cell.street || cell.name;
  return (
    <div>
      {cell.name && (
        <p className="mb-1 font-bold text-grey-dark">
          {unescapeHTML(cell.name)}
        </p>
      )}
      <p className="mb-2">
        {label} {cell.suite_or_apt && <span>Suite {cell.suite_or_apt}</span>}
      </p>
      <p>
        {cell.city}, {cell.state} {cell.zipcode}
      </p>
    </div>
  );
};

const actionsFormatter = (cell, row, rowIndex, extra) => {
  const expired = row.expired || moment().isAfter(row.expiration_date);

  const link = `/booking/${row.id}`;
  return (
    <Translation>
      {(t, { i18n }) => (
        <div className="flex-col flex-wrap justify-around">
          <Link to={link}>
            <button
              href={link}
              style={{ height: '35px' }}
              className="flex items-center text-white button button-green"
              disabled={expired}
            >
              {t('BOOK_SHIPMENT')}
            </button>
          </Link>
          <div className="flex flex-wrap mt-2">
            {extra && extra.onEdit && (
              <button
                className="flex items-center mr-5 text-white button button-blue w-35"
                onClick={() => extra.onEdit(row.id)}
                disabled={expired}
                style={{ marginRight: '8px' }}
              >
                {t('EDIT')}
              </button>
            )}
            {extra && extra.onDelete && (
              <button
                className="block w-10 h-10 text-red-700 rounded-full outline-none bg-red-50 hover:bg-red-100"
                onClick={() => extra.onDelete(row.id)}
              >
                <div className="flex items-center justify-center">
                  <i className="mr-0 fa fa-trash-o" />
                </div>
              </button>
            )}
          </div>
        </div>
      )}
    </Translation>
  );
};

const detailLinkFormatter = (cell, row) => {
  const link = `/quotes/${row._id}`;
  return <Link to={link}>{cell}</Link>;
};

const moneyFormatter = (cell, row) => {
  if (!cell) {
    return <span>none</span>;
  }
  return <span>{formatMoney(cell, row?.currency)}</span>;
};

const wrappedFormatter = (cell) => {
  if (!cell) {
    return <span>none</span>;
  }
  return <span style={{ wordWrap: 'break-word' }}>{cell}</span>;
};

const dateFormat = 'LL';

const dateTime = (cell, _row, _rowIndex, extra) => {
  if (!cell) return <span>None</span>;
  return moment(cell).format(dateFormat);
};

const expirationDate = (cell, _row, _rowIndex, extra) => {
  if (!cell) return <span>None</span>;
  let date = moment(cell).format(dateFormat);
  const expired = moment().isAfter(cell);
  const color = expired ? { color: 'red' } : { color: 'black' };

  return <span style={color}>{date}</span>;
};

export const loadDetailsFormatter = (cell, row) => {
  const rows = [];
  if (row.full_truckload) {
    rows.push(<div key="full-truckload-cell">Full Truckload</div>);
  }

  if (!row.full_truckload && (!row.loads || !row.loads.length)) {
    return <span>None</span>;
  }
  _.each(row.loads, (l, i) => {
    rows.push(
      <div key={i}>
        <LoadDimensionFormatter
          quantity={l.quantity}
          type={l.type}
          length={l.length}
          width={l.width}
          height={l.height}
          dimensional_units={l.dimensional_units}
        />
      </div>,
    );
  });

  rows.push(
    <div key="load-weight" className="mt-2">
      <LoadUnitFormatter
        weight={accounting.format(row.weight)}
        weight_units={row.weight_units}
      />
    </div>,
  );

  return <div>{rows}</div>;
};

const columns = {
  admin: [
    {
      dataField: 'quote_id',
      text: 'Quote ID',
      localizationSlug: 'QUOTE_ID',
      headerStyle: {
        width: '5%',
      },
      formatter: detailLinkFormatter,
    },
    {
      dataField: 'created_at',
      text: 'Created date/time',
      localizationSlug: 'CREATED_DATE_TIME',
      headerStyle: {
        width: '5%',
      },
      formatter: dateTime,
    },
    {
      dataField: 'created_by',
      text: 'Created By',
      localizationSlug: 'CREATED_BY',
      headerStyle: {
        width: '5%',
      },
      formatter: wrappedFormatter,
    },
    // {
    //   dataField: 'shipper.company_name',
    //   text: 'Shipper',
    //   headerStyle: {
    //     width: '10%',
    //   },
    // },
    {
      dataField: 'shipment_type',
      text: 'Type',
      localizationSlug: 'TYPE',
      headerStyle: {
        width: '5%',
      },
    },
    {
      dataField: 'origin[0]',
      text: 'Origin Address',
      localizationSlug: 'ORIGIN_ADDRESS',
      headerStyle: {
        width: '10%',
      },
      formatter: addressFormatter,
    },
    {
      dataField: 'destination[0]',
      text: 'Destination Address',
      localizationSlug: 'DESTINATION_ADDRESS',
      headerStyle: {
        width: '10%',
      },
      formatter: addressFormatter,
    },
    {
      dataField: 'distance',
      text: 'Distance',
      localizationSlug: 'DISTANCE',
      titleize: true,
      headerStyle: {
        width: '4%',
      },
      formatter: distanceFormatter,
    },
    // ex. (Pallets - 3)
    {
      dataField: 'loads',
      text: 'Load',
      localizationSlug: 'LOAD',
      headerStyle: {
        width: '5%',
      },
      formatter: loadDetailsFormatter,
    },
    // Accessorials Dollar Amount
    {
      dataField: 'shipper_accessorial_costs',
      text: 'Accessorials',
      localizationSlug: 'ACCESSORIAL',
      count: 2,
      headerStyle: {
        width: '5%',
      },
      formatter: moneyFormatter,
    },
    // When does the quote expire
    {
      dataField: 'total_cost',
      text: 'Quote Amount',
      localizationSlug: 'QUOTE_AMOUNT',
      headerStyle: {
        width: '5%',
      },
      formatter: moneyFormatter,
    },
    {
      dataField: 'expiration_date',
      text: 'Valid Until',
      localizationSlug: 'VALID_UNTIL',
      headerStyle: {
        width: '5%',
      },
      formatter: expirationDate,
    },
    {
      dataField: 'shipper.id',
      text: 'Actions',
      localizationSlug: 'ACTIONS',
      headerStyle: {
        width: '10%',
      },
      formatter: actionsFormatter,
      formatExtraData: {},
    },
  ],
  shipper: [
    {
      dataField: 'quote_id',
      text: 'QUOTE_ID',
      headerStyle: {
        width: '5%',
      },
      formatter: detailLinkFormatter,
    },
    {
      dataField: 'created_at',
      text: 'CREATED_DATE_TIME',
      headerStyle: {
        width: '5%',
      },
      formatter: dateTime,
    },
    // {
    //   dataField: 'shipper.company_name',
    //   text: 'Shipper',
    //   headerStyle: {
    //     width: '10%',
    //   },
    // },
    {
      dataField: 'shipment_type',
      text: 'TYPE',
      headerStyle: {
        width: '5%',
      },
    },
    {
      dataField: 'origin[0].address',
      text: 'ORIGIN_ADDRESS',
      headerStyle: {
        width: '10%',
      },
    },
    {
      dataField: 'destination[0].address',
      text: 'DESTINATION_ADDRESS',
      headerStyle: {
        width: '10%',
      },
    },
    {
      dataField: 'distance',
      text: 'DISTANCE',
      headerStyle: {
        width: '4%',
      },
      formatter: distanceFormatter,
    },
    // ex. (Pallets - 3)
    {
      dataField: 'loads',
      text: 'LOAD',
      headerStyle: {
        width: '5%',
      },
      formatter: loadDetailsFormatter,
    },
    // Accessorials Dollar Amount
    {
      dataField: 'shipper_accessorial_costs',
      text: 'ACCESSORIAL',
      count: 2,
      headerStyle: {
        width: '5%',
      },
      formatter: moneyFormatter,
    },
    // When does the quote expire
    {
      dataField: 'total_cost',
      text: 'QUOTE_AMOUNT',
      headerStyle: {
        width: '5%',
      },
      formatter: moneyFormatter,
    },
    {
      dataField: 'expiration_date',
      text: 'VALID_UNTIL',
      headerStyle: {
        width: '5%',
      },
      formatter: expirationDate,
    },
    {
      dataField: '_id',
      text: 'ACTIONS',
      headerStyle: {
        width: '10%',
      },
      formatter: actionsFormatter,
      formatExtraData: {},
    },
  ],
};

export default {
  columns,
};
